/**
 * PMC Experience — resident-facing client consumer.
 *
 * Fetches the client-safe subset (branding + doctorAttestation.enabled) from
 * the backend `GET /api/pmc-experience?account_id=` and applies white-label
 * branding as CSS variables on :root. Default branding ({}) removes the vars,
 * so PMCs without a profile look exactly like today (zero change for 7490).
 */

export interface ClientPmcExperience {
  schemaVersion: number;
  branding: {
    displayName?: string;
    logoUrl?: string;
    primaryColor?: string;
    accentColor?: string;
    logoMode?: 'cobrand' | 'replace';
    slug?: string;
  };
  doctorAttestation: { enabled: boolean };
}

const DEFAULT_EXPERIENCE: ClientPmcExperience = {
  schemaVersion: 1,
  branding: {},
  doctorAttestation: { enabled: false },
};

let cached: ClientPmcExperience = DEFAULT_EXPERIENCE;

/** Fired after branding CSS vars are applied (post-login profile fetch). */
export const PMC_EXPERIENCE_EVENT = 'pmc-experience-loaded';

function resolveApiBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:8000/api/';
  }
  return (
    (import.meta as any).env?.VITE_API_URL ||
    'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/'
  );
}

/**
 * Mix a #rrggbb hex toward white (amount > 0) or black (amount < 0) by |amount|.
 * Used to derive hover/tint shades from a PMC's custom colors so we don't hard-
 * code accent/primary variants. Returns undefined for missing/invalid input.
 */
function mixHex(hex: string | undefined, amount: number): string | undefined {
  if (!hex) return undefined;
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return undefined;
  const n = parseInt(m[1], 16);
  const target = amount < 0 ? 0 : 255;
  const p = Math.min(Math.abs(amount), 1);
  const ch = (c: number) => Math.round(c + (target - c) * p).toString(16).padStart(2, '0');
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}

function applyBranding(branding: ClientPmcExperience['branding']): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const set = (key: string, value?: string) => {
    if (value) root.style.setProperty(key, value);
    else root.style.removeProperty(key);
  };
  const primary = branding?.primaryColor;
  const accent = branding?.accentColor || primary;
  set('--pmc-primary', primary);
  set('--pmc-accent', accent);
  // Derived shades — only when a custom color is set; otherwise removed so the
  // :root defaults (which equal today's hardcoded values) win → zero change.
  set('--pmc-accent-hover', accent ? mixHex(accent, -0.12) : undefined);
  set('--pmc-primary-tint', primary ? mixHex(primary, 0.88) : undefined);
  set('--pmc-logo-url', branding?.logoUrl ? `url("${branding.logoUrl}")` : undefined);
}

function notifyBrandingApplied(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PMC_EXPERIENCE_EVENT));
  }
}

export function getPmcExperience(): ClientPmcExperience {
  return cached;
}

export function isDoctorAttestationEnabled(): boolean {
  return Boolean(cached.doctorAttestation?.enabled);
}

export function getBrandingLogoUrl(): string | undefined {
  const url = cached.branding?.logoUrl;
  return url && url.trim() ? url : undefined;
}

export function getBrandingDisplayName(): string | undefined {
  return cached.branding?.displayName;
}

/** 'replace' shows the PMC logo only; 'cobrand' (default) shows PMC × GetPawtected. */
export function getBrandingLogoMode(): 'cobrand' | 'replace' {
  return cached.branding?.logoMode === 'replace' ? 'replace' : 'cobrand';
}

/**
 * Load + cache the resident-facing experience for an account. Safe on failure
 * (keeps the default-safe shell). Returns the resolved experience.
 */
export async function loadPmcExperience(accountId?: number | null): Promise<ClientPmcExperience> {
  if (!accountId || !Number.isInteger(accountId)) {
    cached = DEFAULT_EXPERIENCE;
    applyBranding(cached.branding);
    notifyBrandingApplied();
    return cached;
  }
  try {
    const base = resolveApiBaseUrl();
    const res = await fetch(`${base}pmc-experience?account_id=${accountId}`);
    if (!res.ok) throw new Error(`pmc-experience ${res.status}`);
    const data = await res.json();
    cached = (data?.profile as ClientPmcExperience) || DEFAULT_EXPERIENCE;
    applyBranding(cached.branding || {});
  } catch (e) {
    console.warn('[pmc-experience] load failed; using default-safe shell', e);
    cached = DEFAULT_EXPERIENCE;
    applyBranding(cached.branding);
  }
  notifyBrandingApplied();
  return cached;
}
